import { useState } from 'react';
import { downloadFileIngestion } from '@/api/wells-fargo/wells-fargo-file-ingestion';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { useMutation } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

export function DownloadFileIngestion({
    fileIngestionId,
}: {
    fileIngestionId: number;
}) {
    const [isLoading, setIsLoading] = useState(false);

    const { mutate } = useMutation({
        mutationFn: () => downloadFileIngestion(fileIngestionId),
        onSuccess: (blob: Blob) => {
            // Create a temporary URL for the Blob (CSV file)
            const url = URL.createObjectURL(blob);

            // Create an anchor element and trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.download = `WellsFargoFileIngestion_${fileIngestionId}.csv`; // Name of the downloaded file
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Revoke the object URL after download to free up resources
            URL.revokeObjectURL(url);
            setIsLoading(false);
        },
        onError: () => {
            toast.error(`Error! File could not be downloaded.`, {
                dismissible: true,
            });
            setIsLoading(false);
        },
    });

    const handleDownload = () => {
        setIsLoading(true);
        mutate();
    };

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        className="m-auto"
                        variant="ghost"
                        onClick={handleDownload}
                        disabled={isLoading}
                    >
                        <Download size={16} />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <span>Download File</span>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
